import {
  GetEventClockSeconds,
  GetEventClockTime,
  GetEventStatusPeriodLengthMinutes,
  IsValidEventClockTime,
  SECONDS_IN_MINUTE,
} from '../../configuration'

export type RugbyClockStatusId = number

export type RugbyClockTiming = {
  periodLength: number
  extraPeriodLength: number
  extraTimePlayed: boolean
}

type RugbyClockTimingFn = (timing: RugbyClockTiming) => number

type RugbyClockTimingMap = {
  fromMatchStart: RugbyClockTimingFn
  periodLength: RugbyClockTimingFn
  afterSecondHalf: RugbyClockTimingFn
  afterExtraFirstHalf: RugbyClockTimingFn
  afterExtraTime: RugbyClockTimingFn
  afterPenalties: RugbyClockTimingFn
}

export const rugbyClockTiming: RugbyClockTimingMap = {
  fromMatchStart: (_timing: RugbyClockTiming) => 0,
  periodLength: (timing: RugbyClockTiming) => timing.periodLength,
  afterSecondHalf: (timing: RugbyClockTiming) => timing.periodLength * 2,
  afterExtraFirstHalf: (timing: RugbyClockTiming) =>
    timing.periodLength * 2 + timing.extraPeriodLength,
  afterExtraTime: (timing: RugbyClockTiming) =>
    timing.periodLength * 2 + timing.extraPeriodLength * 2,
  afterPenalties: (timing: RugbyClockTiming) =>
    timing.extraTimePlayed
      ? rugbyClockTiming.afterExtraTime(timing)
      : rugbyClockTiming.afterSecondHalf(timing),
}

export type RugbyClockResolution =
  | { kind: 'fixed'; matchMinutes: number }
  | {
      kind: 'running'
      startOffsetMinutes: number
      periodCapMinutes: number
    }

export type RugbyStatusClockRule = (
  timing: RugbyClockTiming,
) => RugbyClockResolution

export const rugbyClockFixed = (
  minutes: RugbyClockTimingFn,
): RugbyStatusClockRule => {
  return (timing) => ({ kind: 'fixed', matchMinutes: minutes(timing) })
}

export const rugbyClockRunning = (
  startOffsetMinutes: RugbyClockTimingFn,
  periodCapMinutes: RugbyClockTimingFn,
): RugbyStatusClockRule => {
  return (timing) => ({
    kind: 'running',
    startOffsetMinutes: startOffsetMinutes(timing),
    periodCapMinutes: periodCapMinutes(timing),
  })
}

const RUGBY_DEFAULT_CLOCK_RESOLUTION: RugbyClockResolution = {
  kind: 'running',
  startOffsetMinutes: 0,
  periodCapMinutes: 0,
}

function toTiming(
  periodLength: number,
  extraPeriodLength: number,
  extraTimePlayed: boolean,
): RugbyClockTiming {
  return { periodLength, extraPeriodLength, extraTimePlayed }
}

function matchSecondsFromStoredClockTime(
  clockTime: number,
  startOffsetMinutes: number,
  matchTimerInit?: boolean,
): number {
  const offsetSeconds = startOffsetMinutes * SECONDS_IN_MINUTE
  if (offsetSeconds === clockTime || matchTimerInit) return clockTime
  return clockTime + offsetSeconds
}

export const createRugbyEventClockConfig = (
  statusClock: Partial<Record<RugbyClockStatusId, RugbyStatusClockRule>>,
) => {
  const resolve = (statusId: RugbyClockStatusId, timing: RugbyClockTiming) => {
    const rule = statusClock[statusId]
    return rule ? rule(timing) : RUGBY_DEFAULT_CLOCK_RESOLUTION
  }

  const getEventStatusPeriodLengthMinutes: GetEventStatusPeriodLengthMinutes = (
    eventStatusId,
    periodLength,
    extraPeriodLength,
    extraTimePlayed,
  ) => {
    const timing = toTiming(periodLength, extraPeriodLength, extraTimePlayed)
    const resolved = resolve(eventStatusId as RugbyClockStatusId, timing)

    if (resolved.kind === 'fixed') return resolved.matchMinutes

    return resolved.periodCapMinutes
  }

  const getEventPeriodStartOffsetMinutes: GetEventStatusPeriodLengthMinutes = (
    eventStatusId,
    periodLength,
    extraPeriodLength,
    extraTimePlayed,
  ) => {
    const timing = toTiming(periodLength, extraPeriodLength, extraTimePlayed)
    const resolved = resolve(eventStatusId as RugbyClockStatusId, timing)

    if (resolved.kind === 'fixed') return resolved.matchMinutes

    return resolved.startOffsetMinutes
  }

  const getEventClockSeconds: GetEventClockSeconds = (
    clockTime,
    periodLengthMinutes,
    periodLength,
    extraPeriodLength,
    _,
    eventStatusId,
    extraTimePlayed = false,
    matchTimerInit = false,
  ) => {
    const timing = toTiming(periodLength, extraPeriodLength, extraTimePlayed)
    const resolved = resolve(eventStatusId as RugbyClockStatusId, timing)

    if (resolved.kind === 'fixed')
      return (periodLengthMinutes ?? resolved.matchMinutes) * SECONDS_IN_MINUTE

    return matchSecondsFromStoredClockTime(
      clockTime,
      resolved.startOffsetMinutes,
      matchTimerInit,
    )
  }

  const isValidEventClockTime: IsValidEventClockTime = () => true

  const getEventClockTime: GetEventClockTime = (
    minute,
    second,
    periodStartOffsetMinutes,
    _isTimerDirectionDown,
    matchTimerInit,
  ) => {
    const matchSeconds = minute * SECONDS_IN_MINUTE + second
    if (matchTimerInit) return matchSeconds
    const offsetSeconds = (periodStartOffsetMinutes ?? 0) * SECONDS_IN_MINUTE
    const storedSegmentSeconds = matchSeconds - offsetSeconds

    if (storedSegmentSeconds === 0) return matchSeconds

    return storedSegmentSeconds
  }

  return {
    getEventStatusPeriodLengthMinutes,
    getEventPeriodStartOffsetMinutes,
    getEventClockSeconds,
    isValidEventClockTime,
    getEventClockTime,
  }
}
